import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowUpRight,
  Calendar,
  Copy,
  Eye,
  EyeOff,
  FileText,
  Grid3x3,
  Home,
  List,
  Loader2,
  LogOut,
  Plus,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import {
  deleteDiagram,
  duplicateDiagram,
  listUserDiagrams,
  toggleDiagramPublic,
} from '@/services/diagramService'
import type { DiagramListRow } from '@/types/database.types'
import { toast } from '@/stores/toastStore'
import { ConfirmDialog } from '@/components/Dialogs'

type ViewMode = 'grid' | 'list'
type SortMode = 'updated' | 'created' | 'title'

const iconButtonClass =
  'inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:border-slate-600 dark:hover:bg-slate-800 dark:hover:text-white'

const VisibilityBadge = ({ isPublic }: { isPublic: boolean }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
      isPublic
        ? 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/15 dark:bg-emerald-500/10 dark:text-emerald-300'
        : 'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-500/10 dark:bg-slate-800 dark:text-slate-300'
    }`}
  >
    {isPublic ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
    {isPublic ? 'Public' : 'Private'}
  </span>
)

export const MyDiagramsPage: React.FC = () => {
  const [diagrams, setDiagrams] = useState<DiagramListRow[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [viewMode, setViewMode] = useState<ViewMode>('grid')
  const [sortMode, setSortMode] = useState<SortMode>('updated')
  const [diagramToDelete, setDiagramToDelete] = useState<{ id: string; title: string } | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const { user, signOut } = useAuth()
  const navigate = useNavigate()

  const loadDiagrams = async (background = false) => {
    if (background) setRefreshing(true)
    else setLoading(true)
    setError(null)

    const { data, error: loadError } = await listUserDiagrams()
    if (loadError) setError(loadError.message)
    else setDiagrams(data || [])

    setLoading(false)
    setRefreshing(false)
  }

  useEffect(() => {
    if (user) loadDiagrams()
  }, [user])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showUserMenu &&
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setShowUserMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showUserMenu])

  const visibleDiagrams = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    return diagrams
      .filter((diagram) =>
        !query ||
        diagram.title.toLowerCase().includes(query) ||
        diagram.description?.toLowerCase().includes(query)
      )
      .sort((a, b) => {
        if (sortMode === 'title') return a.title.localeCompare(b.title)
        if (sortMode === 'created') {
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        }
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      })
  }, [diagrams, searchQuery, sortMode])

  const handleDeleteDiagram = async () => {
    if (!diagramToDelete || isDeleting) return
    setIsDeleting(true)
    try {
      const { success, error: deleteError } = await deleteDiagram(diagramToDelete.id)
      if (success) {
        setDiagrams((current) => current.filter((diagram) => diagram.id !== diagramToDelete.id))
        setDiagramToDelete(null)
        toast.success('Diagram deleted.')
      } else toast.error(`Failed to delete diagram: ${deleteError?.message}`)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDuplicateDiagram = async (id: string) => {
    const { data, error: duplicateError } = await duplicateDiagram(id)
    if (data) {
      await loadDiagrams(true)
      toast.success('Diagram duplicated.')
    } else toast.error(`Failed to duplicate diagram: ${duplicateError?.message}`)
  }

  const handleTogglePublic = async (id: string, isPublic: boolean) => {
    const { data, error: visibilityError } = await toggleDiagramPublic(id, !isPublic)
    if (data) {
      setDiagrams((current) =>
        current.map((diagram) =>
          diagram.id === id ? { ...diagram, is_public: !isPublic } : diagram
        )
      )
      toast.success(!isPublic ? 'Diagram is now public.' : 'Diagram is now private.')
    } else {
      toast.error(`Failed to update visibility: ${visibilityError?.message}`)
    }
  }

  const formatDate = (value: string) => {
    const date = new Date(value)
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000)
    if (seconds < 60) return 'Just now'
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
    }).format(date)
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/')
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="rounded-2xl border border-slate-200 bg-white px-10 py-8 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <Loader2 className="mx-auto h-7 w-7 animate-spin text-blue-600" />
          <p className="mt-4 text-sm font-medium text-slate-700 dark:text-slate-200">
            Loading your workspace
          </p>
          <p className="mt-1 text-xs text-slate-500">Preparing saved diagrams…</p>
        </div>
      </div>
    )
  }

  const renderThumbnail = (diagram: DiagramListRow, compact = false) => (
    <div className="relative h-full w-full overflow-hidden bg-slate-100 dark:bg-slate-800">
      {diagram.thumbnail ? (
        <img
          src={diagram.thumbnail}
          alt=""
          className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.02]"
        />
      ) : (
        <div className="relative flex h-full items-center justify-center overflow-hidden bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-800 dark:to-slate-900">
          <div className="absolute inset-0 opacity-40 [background-image:linear-gradient(30deg,transparent_49%,rgb(148_163_184/0.2)_50%,transparent_51%),linear-gradient(-30deg,transparent_49%,rgb(148_163_184/0.2)_50%,transparent_51%)] [background-size:32px_18px]" />
          <div className={`relative flex items-center justify-center rounded-xl border border-white/80 bg-white/80 shadow-sm backdrop-blur dark:border-slate-700 dark:bg-slate-800/80 ${compact ? 'h-10 w-10' : 'h-14 w-14'}`}>
            <FileText className={`${compact ? 'h-5 w-5' : 'h-7 w-7'} text-blue-500`} />
          </div>
        </div>
      )}
    </div>
  )

  const renderActions = (diagram: DiagramListRow) => (
    <div className="flex items-center gap-1.5">
      <button
        onClick={() => handleTogglePublic(diagram.id, diagram.is_public)}
        className={iconButtonClass}
        title={diagram.is_public ? 'Make private' : 'Make public'}
        aria-label={diagram.is_public ? 'Make private' : 'Make public'}
      >
        {diagram.is_public ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
      <button
        onClick={() => handleDuplicateDiagram(diagram.id)}
        className={iconButtonClass}
        title="Duplicate diagram"
        aria-label="Duplicate diagram"
      >
        <Copy className="h-4 w-4" />
      </button>
      <button
        onClick={() => setDiagramToDelete({
          id: diagram.id,
          title: diagram.title || 'Untitled diagram',
        })}
        className={`${iconButtonClass} hover:border-red-200 hover:bg-red-50 hover:text-red-600 dark:hover:border-red-900 dark:hover:bg-red-950/40 dark:hover:text-red-400`}
        title="Delete diagram"
        aria-label="Delete diagram"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  )

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
      <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/90 backdrop-blur-xl dark:border-slate-800 dark:bg-slate-950/90">
        <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-10">
            <button onClick={() => navigate('/')} aria-label="Go home">
              <img src="/logo.svg" alt="Degre120" className="h-8 w-auto" />
            </button>
            <nav className="hidden items-center gap-1 md:flex">
              {[
                { label: 'Home', path: '/' },
                { label: 'Editor', path: '/editor' },
                { label: 'Explore', path: '/explore' },
              ].map((item) => (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className="rounded-lg px-3 py-2 text-sm font-medium text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
                >
                  {item.label}
                </button>
              ))}
              <span className="rounded-lg bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">
                My diagrams
              </span>
            </nav>
          </div>

          <div ref={userMenuRef} className="relative">
            <button
              onClick={() => setShowUserMenu((open) => !open)}
              className="flex items-center gap-2.5 rounded-xl border border-transparent px-2 py-1.5 transition hover:border-slate-200 hover:bg-slate-50 dark:hover:border-slate-700 dark:hover:bg-slate-900"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white dark:bg-blue-600">
                {user?.email?.charAt(0).toUpperCase() || 'U'}
              </span>
              <span className="hidden max-w-40 truncate text-sm font-medium text-slate-700 sm:block dark:text-slate-200">
                {user?.email?.split('@')[0] || 'User'}
              </span>
            </button>
            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-64 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 dark:border-slate-700 dark:bg-slate-900">
                <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-800">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Signed in as</p>
                  <p className="mt-1 truncate text-sm font-medium">{user?.email}</p>
                </div>
                <button
                  onClick={handleSignOut}
                  className="flex w-full items-center gap-3 px-4 py-3 text-sm text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  <LogOut className="h-4 w-4" /> Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-10">
        <section className="flex flex-wrap items-end justify-between gap-6 border-b border-slate-200 pb-8 dark:border-slate-800">
          <div className="min-w-0 flex-1">
            <div className="mb-3 flex items-center gap-2 text-sm font-medium text-blue-600 dark:text-blue-400">
              <Home className="h-4 w-4" /> Personal workspace
            </div>
            <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">My diagrams</h1>
            <p className="mt-2 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-400">
              Create, organize, and share your isometric architecture diagrams.
            </p>
          </div>
          <button
            onClick={() => navigate('/editor')}
            className="inline-flex h-11 flex-none items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-blue-600 px-5 text-sm font-semibold text-white shadow-sm shadow-blue-600/20 transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
            style={{ width: 'fit-content' }}
          >
            <Plus className="h-4 w-4" /> New diagram
          </button>
        </section>

        <section className="mt-7 flex flex-wrap items-center justify-between gap-3">
          <div className="relative min-w-[260px] flex-1 md:max-w-lg">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search diagrams"
              className="h-11 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-4 text-sm outline-none transition placeholder:text-slate-400 focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 dark:border-slate-700 dark:bg-slate-900 dark:focus:border-blue-500"
            />
          </div>
          <div className="flex flex-none flex-wrap items-center justify-end gap-2">
            <span className="mr-1 text-sm text-slate-500">
              {visibleDiagrams.length} {visibleDiagrams.length === 1 ? 'diagram' : 'diagrams'}
            </span>
            <select
              value={sortMode}
              onChange={(event) => setSortMode(event.target.value as SortMode)}
              className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-600 outline-none hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
              aria-label="Sort diagrams"
            >
              <option value="updated">Recently updated</option>
              <option value="created">Recently created</option>
              <option value="title">Name</option>
            </select>
            <button
              onClick={() => loadDiagrams(true)}
              className={iconButtonClass}
              title="Refresh diagrams"
              aria-label="Refresh diagrams"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            <div className="flex rounded-lg border border-slate-200 bg-white p-1 dark:border-slate-700 dark:bg-slate-900">
              {(['grid', 'list'] as ViewMode[]).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`flex h-8 w-8 items-center justify-center rounded-md transition ${
                    viewMode === mode
                      ? 'bg-slate-900 text-white shadow-sm dark:bg-blue-600'
                      : 'text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-white'
                  }`}
                  aria-label={`${mode} view`}
                  title={`${mode[0].toUpperCase()}${mode.slice(1)} view`}
                >
                  {mode === 'grid' ? <Grid3x3 className="h-4 w-4" /> : <List className="h-4 w-4" />}
                </button>
              ))}
            </div>
          </div>
        </section>

        {error && (
          <div className="mt-6 flex items-center justify-between rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
            <span>{error}</span>
            <button onClick={() => loadDiagrams()} className="font-semibold hover:underline">Try again</button>
          </div>
        )}

        {visibleDiagrams.length === 0 ? (
          <section className="mt-10 rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-20 text-center dark:border-slate-700 dark:bg-slate-900/50">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-300">
              {searchQuery ? <Search className="h-6 w-6" /> : <FileText className="h-6 w-6" />}
            </div>
            <h2 className="mt-5 text-lg font-semibold">
              {searchQuery ? 'No matching diagrams' : 'Create your first diagram'}
            </h2>
            <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-slate-500">
              {searchQuery
                ? `We couldn't find anything matching “${searchQuery}”.`
                : 'Start with a blank canvas and build your first isometric architecture diagram.'}
            </p>
            {searchQuery ? (
              <button onClick={() => setSearchQuery('')} className="mt-5 text-sm font-semibold text-blue-600 hover:text-blue-700">
                Clear search
              </button>
            ) : (
              <button onClick={() => navigate('/editor')} className="mt-6 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700">
                <Plus className="h-4 w-4" /> New diagram
              </button>
            )}
          </section>
        ) : viewMode === 'grid' ? (
          <section className="mt-7 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {visibleDiagrams.map((diagram) => (
              <article
                key={diagram.id}
                className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-lg hover:shadow-slate-900/5 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-slate-700"
              >
                <button
                  onClick={() => navigate(`/diagram/${diagram.id}`)}
                  className="relative block aspect-[16/10] w-full border-b border-slate-100 text-left dark:border-slate-800"
                >
                  {renderThumbnail(diagram)}
                  <span className="absolute left-3 top-3"><VisibilityBadge isPublic={diagram.is_public} /></span>
                  <span className="absolute bottom-3 right-3 flex h-9 w-9 translate-y-1 items-center justify-center rounded-full bg-slate-950 text-white opacity-0 shadow-lg transition group-hover:translate-y-0 group-hover:opacity-100">
                    <ArrowUpRight className="h-4 w-4" />
                  </span>
                </button>
                <div className="p-4">
                  <button onClick={() => navigate(`/diagram/${diagram.id}`)} className="block w-full text-left">
                    <h2 className="truncate text-base font-semibold tracking-tight transition group-hover:text-blue-600 dark:group-hover:text-blue-400">
                      {diagram.title || 'Untitled diagram'}
                    </h2>
                    <p className="mt-1 line-clamp-2 min-h-[2.5rem] text-sm leading-5 text-slate-500 dark:text-slate-400">
                      {diagram.description || 'No description'}
                    </p>
                  </button>
                  <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3 dark:border-slate-800">
                    <span className="flex items-center gap-1.5 text-xs text-slate-400">
                      <Calendar className="h-3.5 w-3.5" /> {formatDate(diagram.updated_at)}
                    </span>
                    {renderActions(diagram)}
                  </div>
                </div>
              </article>
            ))}
          </section>
        ) : (
          <section className="mt-7 overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div
              className="grid min-w-[820px] gap-5 border-b border-slate-200 bg-slate-50/80 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:border-slate-800 dark:bg-slate-900"
              style={{ gridTemplateColumns: 'minmax(320px, 1fr) 130px 140px 132px' }}
            >
              <span>Diagram</span>
              <span>Visibility</span>
              <span>Last modified</span>
              <span className="text-right">Actions</span>
            </div>
            <div className="min-w-[820px] divide-y divide-slate-100 dark:divide-slate-800">
              {visibleDiagrams.map((diagram) => (
                <article
                  key={diagram.id}
                  className="grid items-center gap-5 px-5 py-4 transition hover:bg-slate-50/80 dark:hover:bg-slate-800/40"
                  style={{ gridTemplateColumns: 'minmax(320px, 1fr) 130px 140px 132px' }}
                >
                  <button onClick={() => navigate(`/diagram/${diagram.id}`)} className="group flex min-w-0 items-center gap-4 text-left">
                    <span className="h-16 w-24 flex-none overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
                      {renderThumbnail(diagram, true)}
                    </span>
                    <span className="min-w-0">
                      <span className="flex items-center gap-2">
                        <span className="truncate text-sm font-semibold group-hover:text-blue-600 dark:group-hover:text-blue-400">{diagram.title || 'Untitled diagram'}</span>
                        <ArrowUpRight className="h-3.5 w-3.5 flex-none text-slate-300 opacity-0 transition group-hover:opacity-100" />
                      </span>
                      <span className="mt-1 block truncate text-xs text-slate-500">{diagram.description || 'No description'}</span>
                    </span>
                  </button>
                  <div><VisibilityBadge isPublic={diagram.is_public} /></div>
                  <span className="flex items-center gap-1.5 text-sm text-slate-500">
                    <Calendar className="h-3.5 w-3.5" /> {formatDate(diagram.updated_at)}
                  </span>
                  <div className="flex justify-end">{renderActions(diagram)}</div>
                </article>
              ))}
            </div>
          </section>
        )}
      </main>
      <ConfirmDialog
        open={diagramToDelete !== null}
        title="Delete diagram?"
        description={
          <>
            <span className="font-medium text-slate-700 dark:text-slate-200">
              {diagramToDelete?.title}
            </span>{' '}
            will be permanently deleted. This action cannot be undone.
          </>
        }
        confirmLabel="Delete diagram"
        destructive
        pending={isDeleting}
        onCancel={() => setDiagramToDelete(null)}
        onConfirm={handleDeleteDiagram}
      />
    </div>
  )
}
